import { useState, useRef, useEffect } from 'react';
import {
    IconButton,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemText,
    Typography,
    Divider,
    CircularProgress,
    Paper,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';
import { notificationsService } from '../../core/services/notifications.service';

interface NotificationType {
    id: string;
    message: string;
    is_read: boolean;
    created_at: string;
}

const mockNotifications: NotificationType[] = [
];

const NotificationsMenu = () => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notifications, setNotifications] = useState<NotificationType[]>([]);


    const fetchNotifications = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await notificationsService.getNotifications();
            setNotifications(data);
        } catch (err) {
            console.error("Fallo al obtener notificaciones:", err);
            setError("No se pudieron cargar las notificaciones.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);


    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // 4. Función para marcar como leída
    const handleMarkAsRead = async (notification: NotificationType) => {
        if (notification.is_read) {
            return; // No hacer nada si ya está leída
        }

        try {
            // Actualización optimista de la UI para una respuesta instantánea
            setNotifications(
                notifications.map((n) =>
                    n.id === notification.id ? { ...n, is_read: true } : n
                )
            );
            // Llamada a la API
            await notificationsService.markAsRead(notification.id);
        } catch (err) {
            console.error("Fallo al marcar como leída:", err);
            // Si la API falla, revertir el cambio en la UI
            setNotifications(
                notifications.map((n) =>
                    n.id === notification.id ? { ...n, is_read: false } : n
                )
            );
        }
    };

    const unreadCount = notifications.filter((n) => !n.is_read).length;
    return (
        <>
            <IconButton ref={anchorRef} onClick={handleOpen} color="inherit">
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ component: Paper, className: 'w-80 md:w-96' }}
            >
                <div className="p-4"><Typography variant="h6">Notificaciones</Typography></div>
                <Divider />

                {loading ? (
                    <div className="flex justify-center items-center p-8"><CircularProgress /></div>
                ) : error ? (
                    <div className="p-8 text-center text-red-600"><Typography>{error}</Typography></div>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <ListItem
                                    key={notification.id}
                                    className={clsx('items-start cursor-pointer hover:bg-gray-100', { 'bg-sky-50': !notification.is_read })}
                                    onClick={() => handleMarkAsRead(notification)}
                                    divider
                                >
                                    <ListItemText
                                        primary={notification.message}
                                        primaryTypographyProps={{ className: clsx('text-sm', { 'font-bold': !notification.is_read }) }}
                                        secondary={formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es })}
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-500"><Typography>No tienes notificaciones</Typography></div>
                        )}
                    </List>
                )}

                <Divider />
                <div className="p-2 text-center">
                    <Typography component="a" href="/notifications" className="text-sm text-blue-600 hover:underline">Ver todas</Typography>
                </div>
            </Popover>
        </>
    );
};

export default NotificationsMenu;